import { QueryDocumentSnapshot } from 'firebase/firestore';
import { CalendarEvent, EventType } from '@/types/community/calendar';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  setDoc
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '../types/users';
import { formatFirestoreTimestamp } from '@/utils/date';
import { removeUndefined } from '@/utils/removeUndefined';
import { loadMockData } from '@/mock';

// Helper functions for mock data
function filterMockData<T extends { title?: string; description?: string; type?: string; status?: string; start?: Date; end?: Date }>(
  data: T[],
  searchTerm?: string,
  type?: string,
  status?: string,
  startDate?: Date,
  endDate?: Date
): T[] {
  return data.filter(item => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (item.title && item.title.toLowerCase().includes(searchLower)) ||
        (item.description && item.description.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }
    
    if (type && item.type !== type) return false;
    if (status && item.status !== status) return false;
    
    if (startDate && item.start && item.start < startDate) return false;
    if (endDate && item.end && item.end > endDate) return false;
    
    return true;
  });
}

function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  
  return {
    data: paginatedData,
    total: data.length
  };
}

// Constants
const CALENDAR_EVENTS_COLLECTION = 'calendar_events';

// Types
export interface CalendarEventsQueryParams {
  searchTerm?: string;
  type?: EventType;
  status?: string;
  startDate?: Date;
  endDate?: Date;
  pageSize?: number;
  page?: number;
  firestoreUser: User;
}

type CalendarEventWithDates = Partial<CalendarEvent>;

// Calendar Events API
export async function getCalendarEvents({
  searchTerm,
  type,
  status,
  startDate,
  endDate,
  pageSize,
  page = 1,
  firestoreUser
}: CalendarEventsQueryParams): Promise<{ events: CalendarEvent[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock calendar events with filters', { searchTerm, type, status, startDate, endDate, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockEvents = await loadMockData<CalendarEvent[]>('events', firestoreUser.language);
    const filteredData = filterMockData(mockEvents, searchTerm, type, status, startDate, endDate);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      events: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }
  
  console.log('API Call: getCalendarEvents', { searchTerm, type, status, startDate, endDate, pageSize, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const eventsRef = collection(db, CALENDAR_EVENTS_COLLECTION);
    
    // Simplified query - just filter by workspace
    const q = query(
      eventsRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );

    const snapshot = await getDocs(q);

    const events = snapshot.docs
      .map(doc => {
        const data = doc.data() as CalendarEventWithDates;
        const mapped: CalendarEventWithDates = {
          ...data,
          id: doc.id,
          start: data.start ? formatFirestoreTimestamp(data.start) ?? undefined : undefined,
          end: data.end ? formatFirestoreTimestamp(data.end) ?? undefined : undefined,
          comments: data.comments || []
        };
        // Only return if all required fields are present
        if (
          typeof mapped.title === 'string' &&
          typeof mapped.description === 'string' &&
          typeof mapped.type === 'string' &&
          mapped.start instanceof Date &&
          mapped.end instanceof Date &&
          mapped.workspace && typeof mapped.workspace.id === 'string'
        ) {
          return mapped as CalendarEvent;
        }
        return undefined;
      })
      .filter((e): e is CalendarEvent => !!e);

    // Apply client-side filters
    let filteredEvents = events;

    // Apply type filter
    if (type) {
      filteredEvents = filteredEvents.filter(event => event.type === type);
    }

    // Apply status filter
    if (status) {
      filteredEvents = filteredEvents.filter(event => event.status === status);
    }

    // Apply date range filters
    if (startDate) {
      filteredEvents = filteredEvents.filter(event => event.start >= startDate);
    }
    if (endDate) {
      filteredEvents = filteredEvents.filter(event => event.end <= endDate);
    }

    // Apply search filter
    if (searchTerm) {
      filteredEvents = filteredEvents.filter(event => 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.notes && event.notes.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply sorting (client-side) - default sort by start date asc
    filteredEvents.sort((a, b) => a.start.getTime() - b.start.getTime());

    // Apply pagination (client-side)
    if (pageSize && pageSize < filteredEvents.length) {
      filteredEvents = filteredEvents.slice(0, pageSize);
    }

    return {
      events: filteredEvents,
      lastDoc: snapshot.docs[snapshot.docs.length - 1],
      total: filteredEvents.length
    };
  } catch (error: any) {
    console.error('Error fetching calendar events:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack,
      firestoreError: error
    });
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to view calendar events');
    } else if (error.code === 'unavailable') {
      throw new Error('Service unavailable: Please try again later');
    } else if (error.code === 'invalid-argument') {
      throw new Error('Invalid query parameters: Please check your filters');
    } else if (error.code === 'failed-precondition') {
      throw new Error('Query failed: Please try again');
    } else if (error.message) {
      throw new Error(`Failed to fetch calendar events: ${error.message}`);
    } else {
      throw new Error('Failed to fetch calendar events: Unknown error occurred');
    }
  }
}

export async function getCalendarEventById(id: string, firestoreUser: User): Promise<CalendarEvent | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock calendar event with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockEvents = await loadMockData<CalendarEvent[]>('events', firestoreUser.language);
    const event = mockEvents.find(e => e.id === id) || null;
    return event;
  }
  
  console.log('API Call: getCalendarEventById', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const eventsRef = collection(db, CALENDAR_EVENTS_COLLECTION);
    const q = query(
      eventsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }

    const eventData = snapshot.docs[0].data() as CalendarEventWithDates;
    const mappedEvent: CalendarEventWithDates = {
      ...eventData,
      start: eventData.start ? formatFirestoreTimestamp(eventData.start) ?? undefined : undefined,
      end: eventData.end ? formatFirestoreTimestamp(eventData.end) ?? undefined : undefined,
      comments: eventData.comments || []
    };
    if (
      typeof mappedEvent.title === 'string' &&
      typeof mappedEvent.description === 'string' &&
      typeof mappedEvent.type === 'string' &&
      mappedEvent.start instanceof Date &&
      mappedEvent.end instanceof Date &&
      mappedEvent.workspace && typeof mappedEvent.workspace.id === 'string'
    ) {
      return mappedEvent as CalendarEvent;
    }
    return null;
  } catch (error) {
    console.error('Error fetching calendar event:', error);
    throw new Error('Failed to fetch calendar event');
  }
}

export async function addCalendarEvent(
  event: Omit<CalendarEvent, 'id'>,
  firestoreUser: User
): Promise<string> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Validate required fields
    if (!event.title?.trim()) {
      throw new Error('Event title is required');
    }
    if (!event.description?.trim()) {
      throw new Error('Event description is required');
    }
    if (!event.type) {
      throw new Error('Event type is required');
    }
    if (!event.start) {
      throw new Error('Event start time is required');
    }
    if (!event.end) {
      throw new Error('Event end time is required');
    }
    if (!event.workspace?.id) {
      throw new Error('Workspace information is required');
    }

    const eventsRef = collection(db, CALENDAR_EVENTS_COLLECTION);
    const newEventRef = doc(eventsRef);
    
    const eventWithWorkspace = addWorkspaceAndActivity(event, firestoreUser, 'created');
    const eventData = removeUndefined({
      ...eventWithWorkspace,
      id: newEventRef.id,
    });

    await setDoc(newEventRef, eventData);
    
    // Create activity record
    await createActivityRecord('created', event, firestoreUser);

    return newEventRef.id;
  } catch (error: any) {
    console.error('Error adding calendar event:', error);
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to create calendar events');
    } else if (error.code === 'unavailable') {
      throw new Error('Service unavailable: Please try again later');
    } else if (error.code === 'invalid-argument') {
      throw new Error('Invalid data provided: Please check all required fields');
    } else if (error.message) {
      throw new Error(`Failed to add calendar event: ${error.message}`);
    } else {
      throw new Error('Failed to add calendar event: Unknown error occurred');
    }
  }
}

export async function updateCalendarEvent(
  id: string,
  data: Partial<Omit<CalendarEvent, 'id'>>,
  firestoreUser: User
): Promise<void> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    if (!id) {
      throw new Error('Event ID is required for update');
    }

    // Get existing event to preserve comments and create activity record
    const existingEvent = await getCalendarEventById(id, firestoreUser);
    if (!existingEvent) {
      throw new Error('Calendar event not found');
    }

    const activityRecord = createActivityRecord('updated', data, firestoreUser);

    const eventsRef = collection(db, CALENDAR_EVENTS_COLLECTION);
    const q = query(
      eventsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Calendar event not found');
    }

    const eventRef = doc(db, CALENDAR_EVENTS_COLLECTION, snapshot.docs[0].id);
    const updateData = removeUndefined({
      ...data,
      comments: data.comments !== undefined ? data.comments : existingEvent.comments,
      activityRecords: [...(existingEvent.activityRecords || []), activityRecord]
    });

    await updateDoc(eventRef, updateData);
  } catch (error: any) {
    console.error('Error updating calendar event:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to update calendar events');
    } else if (error.code === 'not-found') {
      throw new Error('Calendar event not found');
    } else if (error.message) {
      throw new Error(`Failed to update calendar event: ${error.message}`);
    } else {
      throw new Error('Failed to update calendar event: Unknown error occurred');
    }
  }
}

export async function deleteCalendarEvent(
  id: string,
  firestoreUser: User
): Promise<void> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const eventsRef = collection(db, CALENDAR_EVENTS_COLLECTION);
    const q = query(
      eventsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('Calendar event not found');
    }

    const eventRef = doc(db, CALENDAR_EVENTS_COLLECTION, snapshot.docs[0].id);
    await deleteDoc(eventRef);
    
    // Create activity record
    await createActivityRecord('deleted', { id }, firestoreUser);
  } catch (error) {
    console.error('Error deleting calendar event:', error);
    throw new Error('Failed to delete calendar event');
  }
}

export async function getCalendarEventsByType(type: EventType, firestoreUser: User): Promise<CalendarEvent[]> {
  const result = await getCalendarEvents({ type, firestoreUser });
  return result.events;
}

export async function getCalendarEventsByDateRange(startDate: Date, endDate: Date, firestoreUser: User): Promise<CalendarEvent[]> {
  const result = await getCalendarEvents({ startDate, endDate, firestoreUser });
  return result.events;
} 